import dayjs, { Dayjs } from 'dayjs';
import { CountryCode } from 'libphonenumber-js';
import { GetAccessTokenSilently } from './api';
import { Booking } from './booking';

export type Patient = {
  id: number;
  updatedAt: Dayjs;
  createdAt: Dayjs;
  name: string;
  email: string | null;
  phone: string | null;
  ocupation: string | null;
  reason: string | null;
  startDate: string | null; // maybe use DayJS here
  bornDate: string | null; // maybe use DayJS here
  emergencyContactName: string | null;
  emergencyContactEmail: string | null;
  emergencyContactPhone: string | null;
  emergencyContactCountryCode: string | null;
  stripePaymentLink?: string | undefined;
  countryCode?: string;
  isPaymentReminderActive: boolean;
};

export type PatientUpdate = {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  ocupation: string | null;
  reason: string | null;
  startDate: string | null; // maybe use DayJS here
  bornDate: string | null; // maybe use DayJS here
};

export type NewPatient = {
  name: string;
  email: string | null;
  phone: string | null;
  countryCode?: string;
};

export type NewPatientResult = {
  id: number;
  name: string;
  userId: string;
  createdAt: Dayjs;
  updatedAt: Dayjs;
};

export type PatientImportFileResponse = {
  patients: Patient[];
  warnings: {
    message: string;
    row: number;
  }[];
};

export type PatientsInvoiceInfo = { [patientId: string]: { unpaidCount: number; debt: string } };

export interface PatientsMixin {
  fetch: (path: string, init?: RequestInit) => Promise<any>;
  patientsGet: (id: number) => Promise<Patient>;
  patientsList: () => Promise<Patient[]>;
  patientsCreate: (newPatient: NewPatient) => Promise<NewPatientResult>;
  patientsUpdate: (patient: Partial<Patient> & { id: number }) => Promise<Patient>;
  patientsDestroy: (patientId: number) => Promise<undefined>;
  importPatients: (file: File) => Promise<PatientImportFileResponse>;
  exportPatients: () => Promise<any>;
  getPatientPaymentLink: (patientId: number) => Promise<{ url: string }>;
  getBookingsByPatients: () => Promise<PatientsInvoiceInfo>;
  sendPatientReminder: (patientId: number) => Promise<{ message: string }>;
}

export const PatientsMixin = {
  async fetch(path: string, init?: RequestInit): Promise<any> {
    throw new Error('Not implemented');
  },
  async patientsGet(id: number): Promise<Patient> {
    const patient = await this.fetch(`/patients/${id}`);
    patient.createdAt = dayjs(patient.createdAt);
    patient.updatedAt = dayjs(patient.updatedAt);
    return patient;
  },
  async patientsList(): Promise<Patient[]> {
    const patients = await this.fetch('/patients');
    for (const patient of patients) {
      patient.createdAt = dayjs(patient.createdAt);
      patient.updatedAt = dayjs(patient.updatedAt);
    }
    return patients;
  },
  async patientsCreate(patient: NewPatient): Promise<NewPatientResult> {
    const res = await this.fetch('/patients', { method: 'POST', body: JSON.stringify(patient) });
    res.createdAt = dayjs(res.createdAt);
    res.updatedAt = dayjs(res.updatedAt);
    return res;
  },
  async patientsUpdate(patient: Partial<Patient> & { id: number }): Promise<Patient> {
    const res = await this.fetch(`/patients/${patient.id}`, {
      method: 'PUT',
      body: JSON.stringify(patient),
    });
    res.createdAt = dayjs(res.createdAt);
    res.updatedAt = dayjs(res.updatedAt);
    return res;
  },
  async patientsDestroy(patientId: number): Promise<undefined> {
    await this.fetch(`/patients/${patientId}`, { method: 'DELETE' });
  },
  async importPatients(file: File): Promise<PatientImportFileResponse> {
    const base64File = await toBase64(file);

    const patientData = {
      file: base64File,
    };

    const response = await this.fetch('/patients/import', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });

    return response;
  },

  async exportPatients(): Promise<any> {
    const response = await this.fetch('/patients/export', {
      method: 'POST',
    });

    return response;
  },

  async getPatientPaymentLink(patientId: number): Promise<{ url: string }> {
    const res = await this.fetch(`/patients/${patientId}/payment-link`);
    return res;
  },

  async getBookingsByPatients(): Promise<PatientsInvoiceInfo> {
    const res = await this.fetch('/get-bookings-by-patients');
    return res;
  },

  async sendPatientReminder(patientId: number): Promise<{ message: string }> {
    const res = await this.fetch(`/patients/${patientId}/send-payment-reminder`, {
      method: 'post',
    });
    return res;
  },
} as PatientsMixin;

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}
